import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HiServer, 
  HiKey, 
  HiDatabase, 
  HiUsers,           // Icon for User Management
  HiUserAdd,         // Icon for User Register
  HiDocumentReport,  // Icon for Reports
  HiChartPie         // Icon for Analytics
} from "react-icons/hi";
import {
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Box,
  Divider,
  Button,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Container,
  Link as MuiLink,
  Paper
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

// --- EXISTING IMPORTS (Keep your actual file paths here) ---
import Login from "./Logincontainer";
import ApiKeys from "./ApiKeys";
import Containers from "./Containers";
import Images from "./Images";
import Analytics from "./Analytics";

// --- PLACEHOLDER COMPONENTS (Replace these with your actual imports later) ---
const UserRegister = () => <Paper sx={{ p: 3 }}><h2>User Registration Form</h2></Paper>;
const UserManagement = () => <Paper sx={{ p: 3 }}><h2>User Management Table</h2></Paper>;
const Reports = () => <Paper sx={{ p: 3 }}><h2>System Reports</h2></Paper>;

const drawerWidth = 260; // Slightly wider for longer text

function App() {
  const [token, setToken] = useState(localStorage.getItem("jwt") || "");
  // Set default page to Analytics as it's usually the main dashboard view
  const [activePage, setActivePage] = useState("analytics"); 

  // Theme toggle state
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", { noSsr: true });
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark" || prefersDarkMode
  );

  const logout = () => {
    setToken("");
    localStorage.removeItem("jwt");
  };

  // If no token, show Login Page
  if (!token) return <Login setToken={setToken} />;

  // --- MENU CONFIGURATION ---
  const menuItems = [
    { text: "Analytics", key: "analytics", icon: <HiChartPie /> },
    { text: "User Management", key: "user-management", icon: <HiUsers /> },
    { text: "User Register", key: "user-register", icon: <HiUserAdd /> },
    { text: "Reports", key: "reports", icon: <HiDocumentReport /> },
  ];

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: "#6366f1" },
          background: {
            default: darkMode ? "#0f172a" : "#f9fafb",
            paper: darkMode ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.9)",
          },
        },
        typography: {
          fontFamily: "'Inter', 'Roboto', sans-serif",
        },
        shape: { borderRadius: 12 },
      }),
    [darkMode]
  );

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    localStorage.setItem("theme", !darkMode ? "dark" : "light");
  };

  // --- CONTENT SWITCHER ---
  const renderContent = () => {
    switch (activePage) {
      case "analytics":
        return <Analytics token={token} />;
      case "user-management":
        return <UserManagement token={token} />;
      case "user-register":
        return <UserRegister token={token} />;
      case "reports":
        return <Reports token={token} />;
      default:
        return <Analytics token={token} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh", position: "relative", overflow: "hidden" }}>
        {/* Animated Background Gradient */}
        <motion.div
          initial={{ backgroundPosition: "0% 50%" }}
          animate={{ backgroundPosition: "100% 50%" }}
          transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(270deg, #6366f1, #3b82f6, #06b6d4, #9333ea)",
            backgroundSize: "600% 600%",
            zIndex: -1,
            opacity: 0.15,
          }}
        />

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              backdropFilter: "blur(12px)",
              backgroundColor: theme.palette.background.paper,
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <Toolbar>
            <Typography variant="h6" noWrap fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🚀 Dashboard
            </Typography>
          </Toolbar>
          <Divider />
          <List>
            {menuItems.map((item, index) => {
              if (item.type === "divider") {
                return <Divider key={`div-${index}`} sx={{ my: 1 }} />;
              }
              return (
                <motion.div
                  key={item.key}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={activePage === item.key}
                      onClick={() => setActivePage(item.key)}
                      sx={{
                        mx: 1,
                        borderRadius: 2,
                        mb: 0.5,
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "white",
                          "&:hover": {
                            bgcolor: "primary.dark",
                          },
                          "& .MuiListItemIcon-root": {
                            color: "white",
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: "text.primary", minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              );
            })}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Box p={2}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: "100vh", // Fix height to viewport
            overflow: "hidden", // Prevent outer scroll
          }}
        >
          {/* Top Navbar */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              backdropFilter: "blur(12px)",
              bgcolor: theme.palette.background.paper,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" fontWeight="600" color="primary" sx={{ textTransform: "capitalize" }}>
                {activePage.replace("-", " ")} Panel
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton onClick={toggleTheme} color="inherit">
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                   <Typography variant="body2" fontWeight="bold" color="text.primary">
                    Admin User
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Super Admin
                  </Typography>
                </Box>
                <Avatar src="https://api.dicebear.com/7.x/identicon/svg?seed=Admin" />
              </Box>
            </Toolbar>
          </AppBar>

          {/* Page Content Scrollable Area */}
          <Box 
            component="div" 
            flexGrow={1} 
            p={3} 
            sx={{ 
              overflowY: "auto", // Allow inner content to scroll
              position: "relative"
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activePage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: "auto",
              py: 2,
              backdropFilter: "blur(12px)",
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} HarishaCrypto Inc. All rights reserved.
              </Typography>
              <Box display="flex" gap={3}>
                <MuiLink href="#" underline="hover" color="primary">
                  Docs
                </MuiLink>
                <MuiLink href="#" underline="hover" color="primary">
                  Support
                </MuiLink>
                <MuiLink href="#" underline="hover" color="primary">
                  Terms
                </MuiLink>
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;